import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field, fieldInputClass } from '@/components/Field';
import { Modal } from '@/components/Modal';
import { useSchedule } from '@/hooks/use-campaign-flow';
import { describeError } from '@/lib/describe-error';

/**
 * Scheduling a send (spec § 4.1, § 5.5).
 *
 * ── THE 20-MINUTE FLOOR IS NOT ARBITRARY ──────────────────────────────────────────
 *
 * The server refuses anything inside 20 minutes, because SendGrid will not pause or cancel a batch
 * within 10 minutes of its send time — so a tighter schedule produces a send that can never be
 * aborted. That reasoning is worth putting in front of the person choosing the time rather than
 * leaving them to discover it as a rejection, so the input's minimum is set to it and the hint says
 * why.
 *
 * The floor is enforced twice on purpose. The `min` attribute stops the obvious mistake in the
 * picker; the server is still the authority, because `min` is trivially bypassed and a clock can
 * drift between the two.
 *
 * ── LOCAL TIME IN, UTC OUT ────────────────────────────────────────────────────────
 *
 * `datetime-local` has no timezone, so its value is whatever the browser's clock says. Converting
 * through `Date` attaches the browser's offset and `toISOString()` normalises to UTC, which is what
 * the API takes. The label says "your local time" because a scheduling field that does not tell you
 * which timezone it means is the one that sends at 3am.
 */
export function ScheduleSendDialog({
  campaignId,
  campaignName,
  audienceSize,
  onClose,
}: {
  campaignId: string;
  campaignName: string;
  audienceSize: number;
  onClose: () => void;
}) {
  const schedule = useSchedule(campaignId);

  // 25 minutes out, not 20: offering exactly the minimum means the first person who takes a moment
  // to read the dialog gets it rejected for being in the past.
  const earliest = new Date(Date.now() + 25 * 60 * 1000);
  const [at, setAt] = useState(() => toLocalInputValue(earliest));
  const [touched, setTouched] = useState(false);

  const parsed = at.length === 0 ? null : new Date(at);
  const isTooSoon = parsed !== null && parsed.getTime() < Date.now() + 20 * 60 * 1000;

  const error =
    parsed === null || Number.isNaN(parsed.getTime())
      ? 'Choose a date and time.'
      : isTooSoon
        ? 'Pick a time at least 20 minutes from now, so the send can still be cancelled.'
        : undefined;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (error !== undefined || parsed === null) {
      setTouched(true);
      document.getElementById('schedule-at')?.focus();
      return;
    }

    schedule.mutate(parsed.toISOString(), { onSuccess: onClose });
  }

  return (
    <Modal
      title="Schedule this send"
      description={`${audienceSize.toLocaleString()} recipients. You can unschedule it any time before it starts.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={schedule.isPending}>
            Cancel
          </Button>

          <Button type="submit" form="schedule-send" variant="primary" disabled={schedule.isPending}>
            <CalendarClock className="size-4" aria-hidden="true" />
            {schedule.isPending ? 'Scheduling…' : 'Schedule'}
          </Button>
        </>
      }
    >
      <form id="schedule-send" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {schedule.isError && (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink" role="alert">
            {describeError(schedule.error).message}
          </p>
        )}

        <Field
          id="schedule-at"
          label="Send at (your local time)"
          hint="At least 20 minutes from now. Below that, the send could not be cancelled once it started."
          error={touched ? error : undefined}
        >
          <input
            id="schedule-at"
            type="datetime-local"
            value={at}
            min={toLocalInputValue(earliest)}
            onChange={(event) => setAt(event.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={touched && error !== undefined}
            aria-describedby={error === undefined ? 'schedule-at-hint' : 'schedule-at-error'}
            className={fieldInputClass}
          />
        </Field>

        <p className="text-xs text-muted">
          <strong className="font-medium text-foreground">{campaignName}</strong> stays editable until
          it is scheduled. Once scheduled, editing it means unscheduling first — which is deliberate,
          so a change cannot slip into a send somebody has already signed off.
        </p>
      </form>
    </Modal>
  );
}

/**
 * `YYYY-MM-DDTHH:mm` in LOCAL time, which is the only format `datetime-local` accepts.
 *
 * Built by hand rather than by slicing `toISOString()`, because that returns UTC — using it would
 * silently offset the default by the viewer's timezone, so somebody in London would open the dialog
 * in summer and see a time an hour earlier than intended.
 */
function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}
